// hooks/useSkillSuggestions.js - VERSION AVEC ORDRE JSON CONSERVÉ
import { useEffect, useState } from 'react';

// Import each domain's data file from the same level
// IMPORTANT: Ensure these paths are correct relative to useSkillSuggestions.js
import analyseFinanciereSkills from '../../../../../data-skills/skills/analyse-financiere.json';
import digitalTechnologieSkills from '../../../../../data-skills/skills/digital-technologie.json';
import financementSkills from '../../../../../data-skills/skills/financement.json';
import fiscaliteTaxesSkills from '../../../../../data-skills/skills/fiscalite-taxes.json';
import gestionLocativeSkills from '../../../../../data-skills/skills/gestion-locative.json';
import juridiqueAdministratifSkills from '../../../../../data-skills/skills/juridique-administratif.json';
import marcheImmobilierSkills from '../../../../../data-skills/skills/marche-immobilier.json';
import marketingCommercialSkills from '../../../../../data-skills/skills/marketing-commercial.json';
import negociationSkills from '../../../../../data-skills/skills/negociation.json';
import travauxRenovationSkills from '../../../../../data-skills/skills/travaux-renovation.json';

// Metadata defined directly in this file for consistency
const metadata = {
    "version": "1.0",
    "derniereMiseAJour": "2025-07-05", // Updated date
    "nombreDomaines": 10,
    "nombreTotalCompetences": 240, // Updated with all skills
    "niveaux": [
        "debutant",
        "intermediaire",
        "avance",
        "expert" // Ensure 'expert' is included and in the correct order
    ],
    "description": "Catalogue complet des compétences par domaine immobilier"
};

// Fonction utilitaire pour normaliser le niveau de maîtrise
const normalizeNiveauMaitrise = (niveau) => {
    if (!niveau) return 'Débutant';
    
    const mapping = {
        'debutant': 'Débutant',
        'débutant': 'Débutant',
        'beginner': 'Débutant',
        'intermediaire': 'Intermédiaire',
        'intermédiaire': 'Intermédiaire',
        'intermediate': 'Intermédiaire',
        'avance': 'Avancé',
        'avancé': 'Avancé',
        'advanced': 'Avancé',
        'expert': 'Expert'
    };
    
    return mapping[niveau.toLowerCase()] || 'Débutant';
};

// Fonction utilitaire pour transformer les données de compétences
const transformSkillData = (skill, domainId) => {
    return {
        id: skill.id,
        nom_skill: skill.nom,
        description: skill.description || '',
        niveau_maitrise_requis: normalizeNiveauMaitrise(skill.niveau), // Utilise le champ 'niveau' du JSON
        training_link: skill.trainingLink || skill.training_link || '',
        certification: skill.certification || '',
        tags: skill.tags || [],
        prerequis: skill.prerequis || [],
        estimatedHours: skill.estimatedHours || 0,
        objectives: skill.objectives || [],
        domainId: domainId,
        // Conserver le niveau original pour compatibilité
        niveau: skill.niveau,
        // Champs additionnels pour compatibilité
        ...skill
    };
};

export const useSkillSuggestions = () => {
    const [allSkills, setAllSkills] = useState([]);
    const [skillsByDomain, setSkillsByDomain] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        try {
            // Manually assemble skillsByDomain from individual imports
            const assembledSkillsByDomain = {
                "analyse-financiere": analyseFinanciereSkills["analyse-financiere"]?.map(skill => transformSkillData(skill, "analyse-financiere")) || [],
                "digital-technologie": digitalTechnologieSkills["digital-technologie"]?.map(skill => transformSkillData(skill, "digital-technologie")) || [],
                "financement": financementSkills["financement"]?.map(skill => transformSkillData(skill, "financement")) || [],
                "fiscalite-taxes": fiscaliteTaxesSkills["fiscalite-taxes"]?.map(skill => transformSkillData(skill, "fiscalite-taxes")) || [],
                "gestion-locative": gestionLocativeSkills["gestion-locative"]?.map(skill => transformSkillData(skill, "gestion-locative")) || [],
                "juridique-administratif": juridiqueAdministratifSkills["juridique-administratif"]?.map(skill => transformSkillData(skill, "juridique-administratif")) || [],
                "marche-immobilier": marcheImmobilierSkills["marche-immobilier"]?.map(skill => transformSkillData(skill, "marche-immobilier")) || [],
                "marketing-commercial": marketingCommercialSkills["marketing-commercial"]?.map(skill => transformSkillData(skill, "marketing-commercial")) || [],
                "negociation": negociationSkills["negociation"]?.map(skill => transformSkillData(skill, "negociation")) || [],
                "travaux-renovation": travauxRenovationSkills["travaux-renovation"]?.map(skill => transformSkillData(skill, "travaux-renovation")) || [],
            };

            setSkillsByDomain(assembledSkillsByDomain);

            // Create a flat list of all skills
            const flatSkills = Object.keys(assembledSkillsByDomain).reduce((acc, domainId) => {
                const domainSkills = assembledSkillsByDomain[domainId];
                return [...acc, ...domainSkills];
            }, []);

            setAllSkills(flatSkills);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, []);

    // Récupérer les compétences d'un domaine spécifique
    const getSkillsByDomain = (domainId) => {
        return skillsByDomain[domainId] || [];
    };

    // MODIFIÉ : Récupérer les suggestions de compétences pour un domaine - CONSERVE L'ORDRE
    const getSkillSuggestions = (domainId, excludeSkillNames = []) => {
        const domainSkills = getSkillsByDomain(domainId);
        
        // Filter by skill name - garde l'ordre original du JSON
        const filtered = domainSkills.filter(skill => 
            !excludeSkillNames.includes(skill.nom) && 
            !excludeSkillNames.includes(skill.nom_skill)
        );
        
        // Pas de tri ici - conserve l'ordre du JSON
        return filtered;
    };

    // Fonction pour créer une compétence à partir d'une suggestion
    const createSkillFromSuggestion = (suggestion, customData = {}) => {
        const skillData = {
            nom_skill: suggestion.nom_skill || suggestion.nom,
            description: suggestion.description || '',
            niveau_maitrise_requis: suggestion.niveau_maitrise_requis || normalizeNiveauMaitrise(suggestion.niveau),
            rating: 0, // Rating par défaut
            ordre_affichage: 0,
            // Permettre d'override avec des données personnalisées
            ...customData
        };

        // Ajouter training_link seulement s'il existe et n'est pas vide
        if (suggestion.training_link && suggestion.training_link.trim() !== '') {
            skillData.training_link = suggestion.training_link;
        }

        // Ajouter certification seulement s'elle existe et n'est pas vide
        if (suggestion.certification && suggestion.certification.trim() !== '') {
            skillData.certification = suggestion.certification;
        }

        return skillData;
    };

    // Rechercher des compétences par mots-clés
    const searchSkills = (query, domainId = null) => {
        const skills = domainId ? getSkillsByDomain(domainId) : allSkills;
        const searchTerm = query.toLowerCase().trim();

        if (!searchTerm) return skills;

        return skills.filter(skill =>
            (skill.nom && skill.nom.toLowerCase().includes(searchTerm)) ||
            (skill.nom_skill && skill.nom_skill.toLowerCase().includes(searchTerm)) ||
            (skill.description && skill.description.toLowerCase().includes(searchTerm)) ||
            (skill.tags && skill.tags.some(tag => tag.toLowerCase().includes(searchTerm)))
        );
    };

    // Récupérer les compétences par niveau requis
    const getSkillsByLevel = (level, domainId = null) => {
        const skills = domainId ? getSkillsByDomain(domainId) : allSkills;
        const normalizedLevel = normalizeNiveauMaitrise(level);
        return skills.filter(skill => skill.niveau_maitrise_requis === normalizedLevel);
    };

    // Récupérer les compétences prérequises pour une compétence donnée
    const getPrerequisiteSkills = (skillId, domainId) => {
        const domainSkills = getSkillsByDomain(domainId);
        const skill = domainSkills.find(s => s.id === skillId);

        if (!skill || !skill.prerequis || skill.prerequis.length === 0) {
            return [];
        }

        return skill.prerequis.map(prereqId =>
            domainSkills.find(s => s.id === prereqId)
        ).filter(Boolean);
    };

    // Récupérer les compétences qui dépendent d'une compétence donnée
    const getDependentSkills = (skillId, domainId) => {
        const domainSkills = getSkillsByDomain(domainId);
        return domainSkills.filter(skill =>
            skill.prerequis && skill.prerequis.includes(skillId)
        );
    };

    // Récupérer les statistiques des compétences par niveau requis
    const getSkillsStats = (domainId = null) => {
        const skills = domainId ? getSkillsByDomain(domainId) : allSkills;

        const stats = {
            total: skills.length,
            byLevel: {
                debutant: skills.filter(s => s.niveau_maitrise_requis === 'Débutant').length,
                intermediaire: skills.filter(s => s.niveau_maitrise_requis === 'Intermédiaire').length,
                avance: skills.filter(s => s.niveau_maitrise_requis === 'Avancé').length,
                expert: skills.filter(s => s.niveau_maitrise_requis === 'Expert').length
            },
            byLevelOriginal: {
                debutant: skills.filter(s => s.niveau === 'debutant').length,
                intermediaire: skills.filter(s => s.niveau === 'intermediaire').length,
                avance: skills.filter(s => s.niveau === 'avance').length,
                expert: skills.filter(s => s.niveau === 'expert').length
            },
            totalHours: skills.reduce((sum, skill) => sum + (skill.estimatedHours || 0), 0),
            averageHours: skills.length > 0 ? Math.round(skills.reduce((sum, skill) => sum + (skill.estimatedHours || 0), 0) / skills.length) : 0,
            withTraining: skills.filter(s => s.training_link).length,
            withCertification: skills.filter(s => s.certification).length
        };

        return stats;
    };

    // Récupérer une compétence spécifique
    const getSkillById = (skillId, domainId) => {
        const domainSkills = getSkillsByDomain(domainId);
        return domainSkills.find(skill => skill.id === skillId);
    };

    // Récupérer les compétences recommandées basées sur les tags et niveau
    const getRecommendedSkills = (currentSkill, domainId, limit = 3) => {
        if (!currentSkill || !currentSkill.tags) return [];

        const domainSkills = getSkillsByDomain(domainId);

        // Calculer un score de similarité basé sur les tags communs et le niveau
        const skillsWithScore = domainSkills
            .filter(skill => skill.id !== currentSkill.id)
            .map(skill => {
                const commonTags = skill.tags.filter(tag =>
                    currentSkill.tags.includes(tag)
                ).length;
                
                // Bonus si même niveau requis
                const levelBonus = skill.niveau_maitrise_requis === currentSkill.niveau_maitrise_requis ? 0.5 : 0;
                
                return { ...skill, score: commonTags + levelBonus };
            })
            .filter(skill => skill.score > 0)
            .sort((a, b) => b.score - a.score);

        return skillsWithScore.slice(0, limit);
    };

    // Créer un parcours d'apprentissage optimal basé sur les niveaux
    const createLearningPath = (targetSkillId, domainId) => {
        const skill = getSkillById(targetSkillId, domainId);
        if (!skill) return [];

        const path = [];
        const visited = new Set();

        const buildPath = (skillId) => {
            if (visited.has(skillId)) return;
            visited.add(skillId);

            const currentSkill = getSkillById(skillId, domainId);
            if (!currentSkill) return;

            // Ajouter d'abord les prérequis
            if (currentSkill.prerequis) {
                currentSkill.prerequis.forEach(prereqId => {
                    buildPath(prereqId);
                });
            }

            // Puis ajouter la compétence courante
            if (!path.find(s => s.id === skillId)) {
                path.push(currentSkill);
            }
        };

        buildPath(targetSkillId);
        
        // Trier par niveau de difficulté croissant
        const levelOrder = { 'Débutant': 1, 'Intermédiaire': 2, 'Avancé': 3, 'Expert': 4 };
        return path.sort((a, b) => 
            (levelOrder[a.niveau_maitrise_requis] || 1) - (levelOrder[b.niveau_maitrise_requis] || 1)
        );
    };

    // Filtrer les compétences par critères multiples
    const filterSkills = (filters = {}, domainId = null) => {
        let skills = domainId ? getSkillsByDomain(domainId) : allSkills;
        
        if (filters.niveau_requis) {
            skills = skills.filter(skill => skill.niveau_maitrise_requis === filters.niveau_requis);
        }
        
        if (filters.hasTraining) {
            skills = skills.filter(skill => !!skill.training_link);
        }
        
        if (filters.hasCertification) {
            skills = skills.filter(skill => !!skill.certification);
        }
        
        if (filters.tags && filters.tags.length > 0) {
            skills = skills.filter(skill => 
                skill.tags && filters.tags.some(tag => skill.tags.includes(tag))
            );
        }
        
        if (filters.minHours) {
            skills = skills.filter(skill => (skill.estimatedHours || 0) >= filters.minHours);
        }
        
        if (filters.maxHours) {
            skills = skills.filter(skill => (skill.estimatedHours || 0) <= filters.maxHours);
        }
        
        return skills;
    };

    return {
        // États
        allSkills,
        skillsByDomain,
        loading,
        error,

        // Fonctions de base
        getSkillsByDomain,
        getSkillSuggestions, // MODIFIÉ : Returns skills in JSON order
        createSkillFromSuggestion,
        searchSkills,
        getSkillsByLevel,
        getSkillById,

        // Fonctions avancées
        getPrerequisiteSkills,
        getDependentSkills,
        getRecommendedSkills,
        createLearningPath,
        filterSkills,

        // Statistiques
        getSkillsStats,

        // Utilitaires
        normalizeNiveauMaitrise,

        // Métadonnées
        metadata: metadata
    };
};

// Service utilitaire pour récupérer des données sans hook
export const getSkillsData = () => {
    return {
        "analyse-financiere": analyseFinanciereSkills["analyse-financiere"]?.map(skill => transformSkillData(skill, "analyse-financiere")) || [],
        "digital-technologie": digitalTechnologieSkills["digital-technologie"]?.map(skill => transformSkillData(skill, "digital-technologie")) || [],
        "financement": financementSkills["financement"]?.map(skill => transformSkillData(skill, "financement")) || [],
        "fiscalite-taxes": fiscaliteTaxesSkills["fiscalite-taxes"]?.map(skill => transformSkillData(skill, "fiscalite-taxes")) || [],
        "gestion-locative": gestionLocativeSkills["gestion-locative"]?.map(skill => transformSkillData(skill, "gestion-locative")) || [],
        "juridique-administratif": juridiqueAdministratifSkills["juridique-administratif"]?.map(skill => transformSkillData(skill, "juridique-administratif")) || [],
        "marche-immobilier": marcheImmobilierSkills["marche-immobilier"]?.map(skill => transformSkillData(skill, "marche-immobilier")) || [],
        "marketing-commercial": marketingCommercialSkills["marketing-commercial"]?.map(skill => transformSkillData(skill, "marketing-commercial")) || [],
        "negociation": negociationSkills["negociation"]?.map(skill => transformSkillData(skill, "negociation")) || [],
        "travaux-renovation": travauxRenovationSkills["travaux-renovation"]?.map(skill => transformSkillData(skill, "travaux-renovation")) || [],
    };
};

export const getSkillsByDomainId = (domainId) => {
    const allDomainSkills = getSkillsData();
    return allDomainSkills[domainId] || [];
};

export const getSkillMetadata = () => {
    return metadata;
};